import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Circle, ChevronDown, Radio, Navigation, Eye, Truck, Moon, AlertTriangle, Check, MapPin, MapPinOff } from 'lucide-react';
import { BACKEND_URL } from '../config/canonical';
import { toast } from '../hooks/use-toast';

const STATUSES = [
  { key: 'AVAILABLE',     label: 'Available',     color: '#10B981', icon: Radio },
  { key: 'ON_ASSIGNMENT', label: 'On Assignment', color: '#3B82F6', icon: Navigation },
  { key: 'SURVEILLANCE',  label: 'Surveillance',  color: '#8B5CF6', icon: Eye },
  { key: 'TRANSPORT',     label: 'Transport',     color: '#F59E0B', icon: Truck },
  { key: 'OFF_DUTY',      label: 'Off Duty',      color: '#6B7280', icon: Moon },
  { key: 'EMERGENCY',     label: 'Emergency',     color: '#EF4444', icon: AlertTriangle },
];

const getMeta = (key) => STATUSES.find(s => s.key === key) || STATUSES[4];

export default function StatusPill() {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const [status, setStatus] = useState('OFF_DUTY');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gpsState, setGpsState] = useState('idle'); // idle | active | denied | unavailable
  const [lastCoords, setLastCoords] = useState(null);
  const ref = useRef(null);
  const watchIdRef = useRef(null);

  const pushLocation = useCallback(async (latitude, longitude) => {
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/investigators/location?latitude=${latitude}&longitude=${longitude}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setLastCoords({ latitude, longitude, t: Date.now() });
    } catch (e) {
      // non-fatal
    }
  }, [token]);

  const clearLocation = useCallback(async () => {
    if (!token) return;
    try {
      await fetch(`${BACKEND_URL}/api/investigators/location/clear`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      setLastCoords(null);
    } catch (e) { /* ignore */ }
  }, [token]);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const startWatch = useCallback(() => {
    if (!('geolocation' in navigator)) { setGpsState('unavailable'); return; }
    stopWatch();
    setGpsState('idle');
    // Prime with a single reading for instant feedback
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        pushLocation(pos.coords.latitude, pos.coords.longitude);
        setGpsState('active');
      },
      (err) => {
        if (err && err.code === 1) setGpsState('denied');
        else setGpsState('unavailable');
      },
      { enableHighAccuracy: false, maximumAge: 30000, timeout: 15000 }
    );
    // Continuous watch
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        pushLocation(pos.coords.latitude, pos.coords.longitude);
        setGpsState('active');
      },
      (err) => {
        if (err && err.code === 1) setGpsState('denied');
      },
      { enableHighAccuracy: false, maximumAge: 30000, timeout: 30000 }
    );
  }, [pushLocation, stopWatch]);

  const fetchStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/investigators/me/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStatus(data.status || 'OFF_DUTY');
        setLastUpdate(data.last_update || null);
      }
    } catch (e) { /* silent */ }
  }, [token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Start/stop GPS watcher based on current status
  useEffect(() => {
    if (status && status !== 'OFF_DUTY') {
      startWatch();
    } else {
      stopWatch();
      setGpsState('idle');
      clearLocation();
    }
    return () => stopWatch();
  }, [status, startWatch, stopWatch, clearLocation]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const changeStatus = async (next) => {
    if (next === status) { setOpen(false); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/investigators/me/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: next }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || 'Failed to update status');
      setStatus(next);
      setLastUpdate(data?.updated_at || new Date().toISOString());
      toast({ title: 'Status Updated', description: `You are now ${getMeta(next).label}` });
      setOpen(false);
      // Push location immediately if going on-duty
      if (next !== 'OFF_DUTY' && 'geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) => pushLocation(pos.coords.latitude, pos.coords.longitude),
          () => {},
          { enableHighAccuracy: false, maximumAge: 0, timeout: 15000 }
        );
      }
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const meta = getMeta(status);
  const Icon = meta.icon;
  const showGpsDot = status !== 'OFF_DUTY';

  const gpsLabel = {
    idle: 'Acquiring GPS…',
    active: 'GPS live',
    denied: 'Location blocked',
    unavailable: 'GPS unavailable',
  }[gpsState];

  const gpsColor = {
    idle: '#F59E0B',
    active: '#10B981',
    denied: '#EF4444',
    unavailable: '#6B7280',
  }[gpsState];

  return (
    <div className="relative" ref={ref} data-testid="status-pill">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        disabled={saving}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-sm border border-[#334155] bg-[#0B0F19] hover:bg-[#182136] transition-colors text-xs"
        data-testid="status-pill-btn"
        title={showGpsDot ? `${meta.label} • ${gpsLabel}` : meta.label}
      >
        <span className="relative flex items-center justify-center w-2.5 h-2.5">
          <span className="absolute inset-0 rounded-full animate-ping opacity-60" style={{ backgroundColor: meta.color }} />
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
        </span>
        <Icon className="w-3.5 h-3.5 hidden sm:block" style={{ color: meta.color }} />
        <span className="font-heading font-semibold hidden sm:block" style={{ color: meta.color }}>{meta.label}</span>
        <span className="font-heading font-semibold sm:hidden" style={{ color: meta.color }}>{meta.label.split(' ')[0]}</span>
        {showGpsDot && (
          <span className="hidden md:inline-flex items-center gap-1 pl-1.5 ml-0.5 border-l border-[#334155]" data-testid="status-pill-gps">
            {gpsState === 'active' ? <MapPin className="w-3 h-3" style={{ color: gpsColor }} /> : <MapPinOff className="w-3 h-3" style={{ color: gpsColor }} />}
          </span>
        )}
        <ChevronDown className="w-3 h-3 text-[#94A3B8]" />
      </button>

      {open && (
        <div
          className="fixed sm:absolute right-2 sm:right-0 top-[52px] sm:top-11 w-[min(90vw,18rem)] bg-[#131A2A] border border-[#334155] rounded-md shadow-2xl z-[60] max-h-[80vh] overflow-y-auto"
          data-testid="status-pill-menu"
        >
          <div className="px-3 py-2 border-b border-[#334155]">
            <p className="text-[10px] font-mono text-[#94A3B8] uppercase tracking-wider">Change Tracking Status</p>
            <p className="text-[10px] text-[#64748B] mt-0.5">Shares your live GPS with command</p>
          </div>
          <div className="py-1">
            {STATUSES.map((s) => {
              const Si = s.icon;
              const active = s.key === status;
              return (
                <button
                  key={s.key}
                  onClick={() => changeStatus(s.key)}
                  disabled={saving}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-[#F8FAFC] hover:bg-[#182136] transition-colors text-left disabled:opacity-60 min-h-[44px]"
                  data-testid={`status-option-${s.key}`}
                >
                  <Circle className="w-2.5 h-2.5 flex-shrink-0" fill={s.color} stroke={s.color} />
                  <Si className="w-4 h-4 flex-shrink-0" style={{ color: s.color }} />
                  <span className="flex-1 truncate">{s.label}</span>
                  {active && <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
          <div className="px-3 py-2 border-t border-[#334155] space-y-1">
            {showGpsDot ? (
              <div className="flex items-center gap-2 text-[10px]">
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: gpsColor }} />
                <span className="font-mono uppercase tracking-wider" style={{ color: gpsColor }}>{gpsLabel}</span>
                {lastCoords && gpsState === 'active' && (
                  <span className="text-[#64748B] font-mono ml-auto">
                    {lastCoords.latitude.toFixed(4)}, {lastCoords.longitude.toFixed(4)}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-[10px] text-[#64748B] font-mono">GPS paused while Off Duty</p>
            )}
            {gpsState === 'denied' && (
              <p className="text-[10px] text-[#EF4444]">Enable location in your browser to share GPS.</p>
            )}
            {lastUpdate && (
              <p className="text-[10px] text-[#64748B] font-mono">
                Last: {new Date(lastUpdate).toLocaleTimeString()}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
